import { useTranslations } from 'next-intl';
import { QRCodeCanvas } from 'qrcode.react';
import { useEffect } from 'react';

import { useGetUrlWithAnonymousId } from '@flipster/features/src/shared/hooks/useGetUrlWithAnonymousId';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { NoSSR } from '@flipster/react/misc/lifecycle/NoSSR';
import { css } from '@flipster/styled-system/css';
import { divider, hstack, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { appDownloadEvent } from '@flipster/utils/segment/events/app-download';
import { FlipsterApkUrl } from '@flipster/utils/url';

import AppStoreIcon from '@web/assets/svgs/app/appstore.svg';
import GooglePlayIcon from '@web/assets/svgs/app/googleplay.svg';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const AppDownloadModal = ({ isOpen, onClose, onExit }: Props) => {
  const t = useTranslations();
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();

  useEffect(() => {
    segment.track(appDownloadEvent.open);
  }, []);

  return (
    <Modal isOpen={isOpen} size='small' onExit={onExit}>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Common.r251104.Download_app' />} />
      </Modal.Header>
      <Modal.Content css={{ display: 'flex', justifyContent: 'center' }}>
        <div className={vstack({ gap: 40, mb: 24 })}>
          <div className={vstack({ bgColor: 'background.accent.primary', rounded: 8, p: 6, gap: 4 })}>
            <p className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-5' })}>
              <FormattedMessage id='Autogen_common.r251126.s_QR_download' />
            </p>
            <QRCodeCanvas value={getUrlWithAnonymousId('Download')} size={178} includeMargin />
          </div>

          <div className={hstack({ gap: 8 })}>
            <IsomorphicLink
              href={getUrlWithAnonymousId('PlaystoreDownload')}
              onClick={() => segment.track(appDownloadEvent.linkClick, { app_link: 'google_play' })}
              newTab
              aria-label={t('Autogen_common.r251126.s_Get_it_on_google_play_store')}
            >
              <GooglePlayIcon />
            </IsomorphicLink>
            <IsomorphicLink
              href={getUrlWithAnonymousId('Download')}
              onClick={() => segment.track(appDownloadEvent.linkClick, { app_link: 'app_store' })}
              newTab
              aria-label={t('Autogen_common.r251126.s_Get_it_on_apple_store')}
            >
              <AppStoreIcon />
            </IsomorphicLink>
          </div>
          <hr className={divider({ orientation: 'horizontal', color: 'border.default.level-1' })} />

          <div className={vstack({ w: '100%' })}>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3', mb: 20 })}>
              If the Play Store is currently unavailable, please install the APK file.
              <br />
              {'For more information, refer to our '}
              <NoSSR>
                <a
                  className={css({ textStyle: 'Text/M/Underline', color: 'content.accent.info' })}
                  href={zendesk.getUrl('InstallationFlipsterApk')}
                >
                  <FormattedMessage id='Autogen_common.r251126.s_installation_guide' />
                </a>
              </NoSSR>
            </p>
            <Button
              size='medium'
              hierarchy='secondary'
              color='neutral'
              as='a'
              href={FlipsterApkUrl}
              label={<FormattedMessage id='Common.r251104.Download_via_apk' />}
            />
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};
